import { Analytics } from '@cobliteam/analytics';
import type { DateRange, UUID, Vehicle } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';

import { MultiSelectVehicles, SelectTimeWindow } from '@cobliteam/ui-business/components';

export interface FiltersSectionProps {
  period: DateRange;
  vehicles: Vehicle[];
  wantedVehicleIds: UUID[];
  wantedTimeSelection: DateRange;
  onVehicleFilterChanged: (vehicleIds: UUID[]) => void;
  onTimeFilterChanged: (timeSelection: DateRange) => void;
}

export const FiltersSection = ({ period, vehicles, wantedVehicleIds, wantedTimeSelection, ...props }: FiltersSectionProps) => {
  const { t } = useTranslation('tracking');
  const handleVehicleFilterApply = (selectedVehicles: Vehicle[]) => {
    const vehicleIds = selectedVehicles.map((vehicle) => vehicle.id);
    props.onVehicleFilterChanged(vehicleIds);
  };

  const handleTimeFilterApply = (start: number, end: number) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const startHours = startDate.getHours();
    const startMinutes = startDate.getMinutes();

    const endHours = endDate.getHours();
    const endMinutes = endDate.getMinutes();

    const startMillis = startHours * 60 * 60 * 1000 + startMinutes * 60 * 1000 + 1;
    const endMillis = endHours * 60 * 60 * 1000 + endMinutes * 60 * 1000 + 1;

    props.onTimeFilterChanged({ start: startMillis, end: endMillis });
  };

  const handleClearFilter = (filterType: string) => {
    Analytics.track(`Tracking History ${filterType} Filter Cleared`);
  };

  return (
    <div className='flex w-full items-center justify-between gap-12'>
      <div className='flex gap-2'>
        <MultiSelectVehicles
          size='sm'
          selectedVehicles={vehicles.filter((v) => wantedVehicleIds.includes(v.id))}
          onChange={handleVehicleFilterApply}
          onClear={() => handleClearFilter('Vehicles')}
        />
        <SelectTimeWindow size='sm' onTimeChange={handleTimeFilterApply} className='border-none' />
      </div>
      <div className='flex flex-wrap items-center gap-2.5'>
        <div className='flex items-center gap-1'>
          <Icon name='square' className='fill-gray-9 text-gray' />
          <Typography variant='caption-default'>{t('stops')}</Typography>
        </div>
        <div className='flex items-center gap-1'>
          <Icon name='square' className='fill-gray-4 text-gray-4' />
          <Typography variant='caption-default'>{t('vehicle_circulation_period')}</Typography>
        </div>
        <div className='flex items-center gap-1'>
          <Icon name='square' className='fill-warning-9 text-warning-9' />
          <Typography variant='caption-default'>{t('device_disconnected')}</Typography>
        </div>
        <div className='flex items-center gap-1'>
          <Icon name='square' className='fill-success-9 text-success-9' />
          <Typography variant='caption-default'>{t('device_connected')}</Typography>
        </div>
      </div>
    </div>
  );
};
